import React, { useState, useMemo, useEffect } from 'react';
import {
  // feat-057: ChevronRight only used by the hidden Times-excused cell — restore with the column, see CHARTER.
  Check, Search, BookOpen, Mic, CalendarDays, Star, X, /* ChevronRight, */ Info, CalendarOff,
} from 'lucide-react';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import ScorecardNavigation from './ScorecardNavigation';
import StatusChip from './StatusChip';
import StatusReasonCard from './StatusReasonCard';
import EstimatedMarker from './pilot/EstimatedMarker';
import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../contexts/AuthContext';
import { config } from '../../config/environment';
import managementService from '../../services/managementService';
import {
  getTrainingProgress,
  getTrainingActivity,
  TrainingProgressForbiddenError,
  TrainingProgressResponse,
  TrainingProgressRow,
  TrainingStatus,
  TrainingActivityResponse,
  ActivityDay,
  FAKE_TRAINING_LOCATIONS,
} from '../../services/trainingProgressService';

// Shared roster grid template (header + rows must match). 9 columns:
// Crew · Location · Lessons · Recorded · Worked · Lesson % · Recorded % · Status · Last active
// feat-084: every track is minmax(0,…) so a long unbreakable cell (e.g. a long crew
// name) truncates inside its track instead of an implicit minmax(auto,…) blowing the
// track out and shoving every later column right (root cause of the alignment bug, #2).
// The two % columns render a tiny string (100% / 67% / —) so 0.75fr is ample.
// NOTE (feat-057): the legacy Accuracy + Times-excused columns stay HIDDEN — these two
// % columns are NEW (last-2-weeks adherence), not those restored.
const GRID = 'minmax(0,1.7fr) minmax(0,1.0fr) minmax(0,0.8fr) minmax(0,0.8fr) minmax(0,0.8fr) minmax(0,0.75fr) minmax(0,0.75fr) minmax(0,1.9fr) minmax(0,0.9fr)';

// feat-195: StatusChip moved to ./StatusChip so StatusReasonCard can render it in the
// hover card's header band without importing this page (which imports the card).

// feat-195 D5: ONE <Tooltip> instance for the whole roster (not one per row), mounted
// OUTSIDE the overflow-x-auto wrapper. Both halves matter:
//  • Outside the wrapper — `overflow-x: auto` computes `overflow-y` to `auto` too, so an
//    in-flow popover is clipped on BOTH axes. That clip is the documented reason the
//    column-header tooltips are still native `title`; mounting at document level is what
//    escapes it.
//  • Not a child of the Status cell — the card therefore cannot contribute to sizing the
//    grid's 1.9fr Status track. The feat-084 alignment bug is prevented BY CONSTRUCTION
//    here, not by care, so no max-w band-aid on the cell is needed or wanted.
const STATUS_TOOLTIP_ID = 'training-status-reason';

// ── Real-column counter (coral when lagging, success when boosting) ───────────
function Counter({ value, Icon, lagging, boost }: { value: number; Icon: React.ElementType; lagging: boolean; boost: boolean }) {
  const tone = lagging ? 'text-coral' : boost ? 'text-success' : 'text-inkMuted';
  const valueTone = lagging ? 'text-coral' : boost ? 'text-success' : 'text-ink';
  return (
    <div className="flex items-center gap-1.5">
      <Icon size={14} className={tone} strokeWidth={2.5} />
      <span className={`text-sm font-bold ${valueTone}`}>{value}</span>
    </div>
  );
}

// ── Last-2-weeks adherence % cell (feat-105 totals basis, ADR 0001 option (b)) ─
// PURE FORMATTER: the backend already returns the final pre-capped percentage
// (feedback_metrics_single_source). React does ZERO arithmetic here — no num/den,
// no Math.round, no cap. An uncapped >100% can only leak if division survives, so
// there is none. `null` (no worked days in window) → honest "—" (no 0%, no NaN),
// reusing the page's muted-dash pattern. Neutral text-ink only — NOT the preview-*
// slate wash (that means seeded data and would lie about real numbers) and NOT
// success-green (per design-lead honesty rule).
function AdherenceCell({ pct }: { pct: number | null }) {
  if (pct == null) {
    return <span className="text-sm text-inkMuted/40" title="No days on the Square clock in the last 2 weeks">—</span>;
  }
  return <span className="text-sm font-bold text-ink">{pct}%</span>;
}

// ── feat-095: recent-activity timeline ────────────────────────────────────────
// Renders ONE backend ActivityDay verbatim — no bucketing, no %/day math
// (feedback_metrics_single_source). Reuses the page's icon vocabulary
// (CalendarDays=worked, Mic=recorded, BookOpen=lesson) and color semantics: an
// active flag is success-toned, an inactive one is muted. The completed-unit
// title(s) render below the indicators when present.
function formatActivityDate(iso: string): string {
  // Parse the "YYYY-MM-DD" location-local date as a calendar date (no tz shift —
  // it is already location-local from the backend). Render e.g. "Sat, Jun 20".
  const [y, m, d] = iso.split('-').map((n) => parseInt(n, 10));
  if (!y || !m || !d) return iso;
  const dt = new Date(y, m - 1, d);
  if (Number.isNaN(dt.getTime())) return iso;
  return dt.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

function ActivityFlag({ on, Icon, label }: { on: boolean; Icon: React.ElementType; label: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-semibold ${on ? 'text-success' : 'text-inkMuted/40'}`}
      title={on ? label : `No ${label.toLowerCase()}`}
    >
      <Icon size={13} strokeWidth={2.5} />
    </span>
  );
}

function ActivityDayRow({ day }: { day: ActivityDay }) {
  return (
    <div className="flex flex-col gap-1 rounded-lg px-3 py-2 bg-cream border border-softBorder">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-bold text-ink">{formatActivityDate(day.date)}</span>
        {/* Lessons → Recorded → Worked, matching the status-line / column order. */}
        <div className="flex items-center gap-3">
          <ActivityFlag on={day.lessons.length > 0} Icon={BookOpen} label="Lesson" />
          <ActivityFlag on={day.recorded} Icon={Mic} label="Recorded" />
          <ActivityFlag on={day.worked} Icon={CalendarDays} label="Worked" />
        </div>
      </div>
      {day.lessons.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pl-0.5">
          {day.lessons.map((l) => (
            <span
              key={l.lesson_id}
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold bg-success-light text-success"
            >
              <BookOpen size={11} strokeWidth={2.5} /> {l.title}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// feat-058: hidden with the Times-excused column (feat-057) — restore together.
// ── "Excused (sample)" placeholder qualifier (Slate — annotation, NOT a status flip) ──
// function ExcusedQualifier() {
//   return (
//     <span
//       className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-semibold bg-preview-light text-preview"
//       title="Seeded preview — not yet recorded from your data."
//     >
//       <Info size={11} strokeWidth={2.2} /> Excused (sample)
//     </span>
//   );
// }

// feat-057: only used by the hidden Accuracy column — kept (commented) for easy restore, see CHARTER.
// function accuracyTone(accuracy: number | null): string {
//   if (accuracy === null) return 'text-inkMuted';
//   if (accuracy >= 90) return 'text-success';
//   if (accuracy >= 75) return 'text-gold';
//   return 'text-coral';
// }

// ── Loading skeleton ──────────────────────────────────────────────────────────
function Skeleton() {
  return (
    <div className="overflow-hidden rounded-2xl bg-white border border-softBorder animate-pulse">
      <div className="h-11 border-b border-softBorder bg-cream/60" />
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="h-16 border-b border-softBorder last:border-0 bg-cream/40" />
      ))}
    </div>
  );
}

// ── Filters ───────────────────────────────────────────────────────────────────
type FilterId = 'all' | 'behind' | 'ontrack' | 'above';

const TrainingProgressPage: React.FC = () => {
  const { selectedTenant } = useTenant();
  const { user } = useAuth();

  const FAKE = config.environment === 'fake';

  const [data, setData] = useState<TrainingProgressResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [filter, setFilter] = useState<FilterId>('all');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  // ── feat-095: per-member recent-activity timeline (lazy, fetched on dialog open) ──
  const [activity, setActivity] = useState<TrainingActivityResponse | null>(null);
  const [activityLoading, setActivityLoading] = useState(false);
  // Soft-fail only: a fetch failure leaves the rest of the dialog rendered (AC#7).
  const [activityFailed, setActivityFailed] = useState(false);

  // ── Location filter (F3/F4) ──────────────────────────────────────────────
  // Admin/Corporate only. The selection is forwarded to the endpoint as
  // `location_id`, so the backend re-scopes the roster AND the summary tiles
  // (server-side filter — never a client-side row hide, which would desync the
  // tiles). Store-managers are pinned server-side and never see the control.
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [availableLocations, setAvailableLocations] = useState<Array<{ id: string; name: string }>>([]);

  // Role from the management profile (mirrors ServerScorecard.normalizedRole).
  // Corporate is treated identically to Admin for "can filter freely" (RBAC convention).
  const normalizedRole = useMemo(() => {
    const rawRole = user?.managementUser?.role;
    if (!rawRole) return null;
    return rawRole.charAt(0).toUpperCase() + rawRole.slice(1).toLowerCase();
  }, [user?.managementUser?.role]);
  const isAdminOrCorporate = normalizedRole === 'Admin' || normalizedRole === 'Corporate';
  // Fake mode has no real role; show the filter so it is demonstrable in `npm run start:fake`.
  const canFilterByLocation = FAKE || isAdminOrCorporate;

  // Reset the selection when the tenant changes — its locations differ.
  useEffect(() => {
    setSelectedLocationId(null);
  }, [selectedTenant]);

  // Populate the dropdown from a STABLE tenant-locations list (not from data.rows,
  // which collapse to one location once filtered). Reuses the scorecard source.
  useEffect(() => {
    if (FAKE) { setAvailableLocations(FAKE_TRAINING_LOCATIONS); return; }
    if (!isAdminOrCorporate) return;
    const tenantId = selectedTenant || user?.managementUser?.tenant_id;
    if (!tenantId) return;
    let cancelled = false;
    managementService.getLocationsByTenant(tenantId)
      .then((resp) => {
        if (cancelled) return;
        if (resp.success && resp.locations) {
          // Only show locations that have gone live (bespoke_go_live_date set).
          setAvailableLocations(
            resp.locations
              .filter((l: any) => l.bespoke_go_live_date != null)
              .map((l: any) => ({ id: l.id, name: l.name }))
          );
        }
      })
      .catch(() => { /* non-blocking — a missing list just leaves "All locations" */ });
    return () => { cancelled = true; };
  }, [FAKE, isAdminOrCorporate, selectedTenant, user?.managementUser?.tenant_id]);

  useEffect(() => {
    if (!selectedTenant) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setForbidden(false);
    getTrainingProgress(selectedTenant, selectedLocationId ? { locationId: selectedLocationId } : {})
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof TrainingProgressForbiddenError) setForbidden(true);
        else setError('Could not load training progress.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedTenant, selectedLocationId]);

  // Esc closes the detail modal.
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setSelected(null);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  // feat-095: fetch the recent-activity timeline when the dialog opens (keyed on
  // `selected`). Degrades gracefully — any failure (incl. the fail-closed 404/403
  // TrainingProgressForbiddenError) just hides the section; the rest of the dialog
  // still renders (AC#7). React renders the backend flags/titles verbatim.
  useEffect(() => {
    if (!selected || !selectedTenant) {
      setActivity(null);
      setActivityFailed(false);
      return;
    }
    let cancelled = false;
    setActivity(null);
    setActivityFailed(false);
    setActivityLoading(true);
    getTrainingActivity(selectedTenant, selected)
      .then((a) => { if (!cancelled) setActivity(a); })
      .catch(() => { if (!cancelled) setActivityFailed(true); })
      .finally(() => { if (!cancelled) setActivityLoading(false); });
    return () => { cancelled = true; };
  }, [selected, selectedTenant]);

  const summary = data?.summary ?? null;

  const filters: Array<{ id: FilterId; label: string; n: number }> = [
    { id: 'all', label: 'All crew', n: summary?.crew_count ?? 0 },
    { id: 'behind', label: 'Behind', n: summary?.behind ?? 0 },
    { id: 'ontrack', label: 'On track', n: summary?.on_track ?? 0 },
    { id: 'above', label: 'Above & beyond', n: summary?.above ?? 0 },
  ];

  const rows = useMemo(() => {
    if (!data) return [] as TrainingProgressRow[];
    // feat-112: "On track" matches not-behind AND not-neutral, mirroring the
    // summary.on_track count (which now excludes no_recent_shifts). Neutral crew
    // appear only under "All crew" — never in a judgmental bucket.
    const matchesFilter = (s: TrainingStatus) =>
      filter === 'all' ? true
        : filter === 'behind' ? s === 'behind'
          : filter === 'ontrack' ? (s !== 'behind' && s !== 'no_recent_shifts')
            : filter === 'above' ? s === 'above'
              : true;
    return data.rows
      .filter((r) => matchesFilter(r.status) && r.name.toLowerCase().includes(query.toLowerCase()))
      .sort((a, b) => {
        // Behind first, then Above, then On track, then neutral (absent crew) last.
        const rank = (s: TrainingStatus) => (s === 'behind' ? 0 : s === 'above' ? 1 : s === 'on_track' ? 2 : 3);
        return rank(a.status) - rank(b.status) || b.worked - a.worked;
      });
  }, [data, filter, query]);

  const sel = selected && data ? data.rows.find((r) => r.server_id === selected) ?? null : null;

  // feat-195: the single <Tooltip> resolves its anchor's `data-tooltip-content`
  // (the server_id) back to a row through this map — same indirection as
  // ShiftMetricsTable's Score tooltip. Keyed on `data`, not on the filtered/sorted
  // `rows`, so the lookup cannot miss while a filter transition is in flight.
  const rowsById = useMemo(() => {
    const m = new Map<string, TrainingProgressRow>();
    (data?.rows ?? []).forEach((r) => m.set(r.server_id, r));
    return m;
  }, [data]);

  // feat-177: the recorded-vs-worked tile is the only tile carrying a tooltip, because
  // it is the only one whose number is not self-describing. Both sides are all-time day
  // counts — distinct recorded days over distinct Square worked days — and there is NO
  // cap: recorded days are not a subset of worked days, so this tile can legitimately
  // read over 100%. The tooltip has to say WHY, or a manager reads 117% as a bug.
  const tiles = summary
    ? [
      { value: `${summary.on_track}`, sub: `/ ${summary.crew_count} crew`, accent: 'text-success', label: 'On track — meeting the goal', icon: null as React.ReactNode, tip: null as string | null },
      { value: `${summary.behind}`, sub: null, accent: 'text-coral', label: 'Behind — a number is lagging', icon: null, tip: null },
      { value: `${summary.above}`, sub: null, accent: 'text-success', label: 'Above & beyond — extra units', icon: <Star size={18} className="text-success" fill="#5FA845" />, tip: null },
      {
        value: `${summary.recorded_vs_worked_pct}%`, sub: null, accent: 'text-ink',
        label: 'Days recorded vs days worked',
        icon: <Mic size={18} className="text-gold" />,
        tip: 'Across all crew and all time, days on which at least one shift was recorded, as a percentage of days worked on the Square clock. This can go above 100% — recordings made on days off the Square clock count too.',
      },
    ]
    : [];

  return (
    <div className="min-h-screen bg-cream">
      <ScorecardNavigation />

      <main className="mx-auto max-w-7xl px-4 md:px-8 py-8">
        {/* Page header + goal pill */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <h1 className="font-display text-2xl text-ink">Training Progress</h1>
          <div className="flex items-center gap-2 rounded-full px-3.5 py-2 text-xs font-semibold bg-coral-light text-coral font-sans">
            <Check size={14} strokeWidth={2.5} /> Goal: keep lessons and recordings on pace with days worked
          </div>
        </div>

        {forbidden ? (
          <div className="rounded-2xl bg-white border border-softBorder p-10 text-center">
            <h3 className="font-display text-lg text-ink mb-1">Training progress unavailable</h3>
            <p className="text-inkMuted text-sm">This view isn't available for the selected tenant.</p>
          </div>
        ) : loading && !data ? (
          <Skeleton />
        ) : error && !data ? (
          <div className="rounded-2xl bg-white border border-softBorder p-10 text-center">
            <p className="text-inkMuted text-sm">{error}</p>
          </div>
        ) : data && summary ? (
          <>
            {/* Summary tiles (REAL — NO placeholder marker, ADR D3) */}
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4 mb-7">
              {tiles.map((t, i) => (
                <div key={i} className="rounded-2xl p-5 bg-white border border-softBorder">
                  <div className="flex items-baseline gap-2">
                    <span className={`text-3xl font-extrabold tracking-tight font-display ${t.accent}`}>{t.value}</span>
                    {t.sub && <span className="text-lg font-bold text-inkMuted font-sans">{t.sub}</span>}
                    {t.icon}
                  </div>
                  {/* feat-177: the affordance mirrors the column headers (native title —
                      immune to clipping, dotted underline + cursor-help so it is
                      discoverable at rest, tabIndex for keyboard reach). Tiles without a
                      tip stay plain text so the underline still MEANS "there is more here". */}
                  {t.tip ? (
                    <div
                      tabIndex={0}
                      title={t.tip}
                      className="mt-1 text-xs font-medium text-inkMuted font-sans cursor-help underline decoration-dotted decoration-inkMuted/40 underline-offset-2 w-fit"
                    >
                      {t.label}
                    </div>
                  ) : (
                    <div className="mt-1 text-xs font-medium text-inkMuted font-sans">{t.label}</div>
                  )}
                </div>
              ))}
            </div>

            {/* Toolbar: filters + search */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="flex flex-wrap items-center gap-2">
                {filters.map((f) => {
                  const active = filter === f.id;
                  return (
                    <button
                      key={f.id}
                      onClick={() => setFilter(f.id)}
                      aria-pressed={active}
                      className={`rounded-full px-3.5 py-1.5 text-sm font-bold transition-colors font-sans border ${active ? 'bg-ink text-white border-ink' : 'bg-white text-inkMuted border-softBorder hover:border-coral hover:text-coral'}`}
                    >
                      {f.label}<span className="opacity-60 ml-1.5">{f.n}</span>
                    </button>
                  );
                })}
                {/* feat-112: low-key subordinate count (treatment (a)) — neutral crew
                    with no completed Square shifts in the last 2 weeks. NOT a pill: it
                    never filters and carries no active treatment; it just discloses the
                    cohort excluded from the three buckets. Rendered from the backend
                    tally verbatim (single-source), shown only when non-zero. */}
                {summary.no_recent_shifts > 0 && (
                  <span
                    className="inline-flex items-center gap-1.5 px-1 text-xs font-semibold text-inkMuted font-sans"
                    title="No completed shifts on the Square clock in the last 2 weeks — set aside from the Behind / On track / Above & beyond counts."
                  >
                    <CalendarOff size={13} strokeWidth={2.5} className="text-inkMuted/70" />
                    No recent shifts · {summary.no_recent_shifts}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {/* Location filter — Admin/Corporate only (F4). Store-managers are
                    pinned server-side; we show their location name as static text. */}
                {canFilterByLocation ? (
                  <select
                    value={selectedLocationId ?? ''}
                    onChange={(e) => setSelectedLocationId(e.target.value || null)}
                    aria-label="Filter by location"
                    className="rounded-full px-3.5 py-2 text-sm font-semibold bg-white border border-softBorder text-ink font-sans outline-none focus:border-coral focus:ring-2 focus:ring-coral/30"
                  >
                    <option value="">All locations</option>
                    {availableLocations.map((loc) => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>
                ) : data && data.rows[0]?.location_name ? (
                  <span className="rounded-full px-3.5 py-2 text-sm font-semibold bg-white border border-softBorder text-inkMuted font-sans">
                    {data.rows[0].location_name}
                  </span>
                ) : null}
                <div className="flex items-center gap-2 rounded-full px-3.5 py-2 bg-white border border-softBorder">
                  <Search size={15} className="text-inkMuted" />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search crew"
                    aria-label="Search crew"
                    className="w-32 bg-transparent text-sm outline-none text-ink font-sans"
                  />
                </div>
              </div>
            </div>

            {/* Roster table — one overflow-x-auto wrapper so the header and rows
                (which share the GRID const) scroll together and never desync; the
                inner min-w floor keeps columns readable, scrolling below it (OQ1). */}
            <div className="overflow-x-auto rounded-2xl bg-white border border-softBorder">
             <div className="min-w-[920px]">
              {/* Header — two tiers sharing the GRID template: a "Last 2 weeks" group
                  eyebrow spanning the two % columns (CSS grid has no colspan, so it is
                  one item with gridColumn:'span 2'), then the per-column labels (OQ2). */}
              <div className="hidden md:block border-b border-softBorder">
                <div
                  className="grid items-end gap-3 px-5 pt-3 text-[9px] font-bold uppercase tracking-widest text-inkMuted/60 font-sans"
                  style={{ gridTemplateColumns: GRID }}
                >
                  <span /><span /><span /><span /><span />
                  <span className="text-center" style={{ gridColumn: 'span 2' }}>Last 2 weeks</span>
                  <span /><span />
                </div>
                <div
                  className="grid items-center gap-3 px-5 pb-3 pt-1 text-[10px] font-bold uppercase tracking-widest text-inkMuted/60 font-sans"
                  style={{ gridTemplateColumns: GRID }}
                >
                  <span>Crew member</span>
                  <span>Location</span>
                  {/* The 5 explained headers carry a native-title tooltip (immune to the
                      overflow-x clip a custom popover would hit) + a dotted-underline /
                      cursor-help affordance so the tooltip is discoverable at rest, and
                      tabIndex={0} for keyboard reach (P2-B). Crew/Location/Status/Last
                      active are self-evident and stay plain. Zero new tokens. */}
                  <span tabIndex={0} title="Number of lessons completed all-time (a lesson = a full Learn unit, every part finished)" className="cursor-help underline decoration-dotted decoration-inkMuted/40 underline-offset-2">Lessons</span>
                  <span tabIndex={0} title="Number of unique days on which the user recorded at least one shift (all-time). Recording more than one shift in a day still counts as one day." className="cursor-help underline decoration-dotted decoration-inkMuted/40 underline-offset-2">Recorded</span>
                  <span tabIndex={0} title="Number of days worked — the unique dates on or after this location's Bespoke training go-live date on which the user completed (clocked out of) a Square shift" className="cursor-help underline decoration-dotted decoration-inkMuted/40 underline-offset-2">Worked</span>
                  <span tabIndex={0} title="Within the last two weeks, of the days the user was on the Square clock, the percentage on which they completed a lesson" className="cursor-help underline decoration-dotted decoration-inkMuted/40 underline-offset-2">Lesson %</span>
                  <span tabIndex={0} title="Within the last two weeks, of the days the user was on the Square clock, the percentage of days on which they recorded at least one shift" className="cursor-help underline decoration-dotted decoration-inkMuted/40 underline-offset-2">Recorded %</span>
                  {/* feat-195 AC2 — the Status header was the ONLY one with no
                      explanation of its own column. It keeps the header treatment
                      (native title, immune to the overflow clip) rather than becoming a
                      second floating layer on the same row of chrome: this header
                      carries the STATIC RULE, the per-row card carries THAT PERSON'S
                      DATA. One sentence of rule, one of carve-out (design §2.6). */}
                  <span tabIndex={0} title="Status compares the last 2 weeks: crew are expected to complete one lesson and record one shift for each day they work, with one day of grace. Crew who have finished every lesson in the curriculum are paced on recordings only." className="cursor-help underline decoration-dotted decoration-inkMuted/40 underline-offset-2">Status</span>
                  <span>Last active</span>
                </div>
              </div>

              {rows.map((p, idx) => {
                const maxv = Math.max(p.units, p.recorded, p.worked);
                const lag = (v: number) => p.status === 'behind' && v < maxv;
                const rowBg = p.status === 'behind' ? 'bg-coral-light/30' : p.status === 'above' ? 'bg-success-light/30' : 'bg-transparent';
                return (
                  <div
                    key={p.server_id}
                    className={`grid items-center gap-3 px-5 py-4 font-sans ${rowBg} ${idx < rows.length - 1 ? 'border-b border-softBorder' : ''}`}
                    style={{ gridTemplateColumns: GRID }}
                  >
                    {/* Name */}
                    <button onClick={() => setSelected(p.server_id)} className="group flex items-center gap-3 text-left">
                      <div
                        className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
                        style={{ background: p.status === 'behind' ? '#D55847' : p.status === 'no_recent_shifts' ? '#717182' : '#5FA845' }}
                      >
                        {p.initials}
                        {p.status === 'above' && (
                          <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-white">
                            <Star size={11} className="text-success" fill="#5FA845" />
                          </span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="truncate text-sm font-bold text-ink group-hover:underline" title={p.name}>{p.name}</div>
                        <div className="text-xs text-inkMuted">Crew</div>
                      </div>
                    </button>

                    {/* Location — REAL (backend location.name); no Slate marker */}
                    <span className="truncate text-sm text-inkMuted font-sans" title={p.location_name}>{p.location_name}</span>

                    {/* REAL columns — clean */}
                    <Counter value={p.units} Icon={BookOpen} lagging={lag(p.units)} boost={p.status === 'above'} />
                    <Counter value={p.recorded} Icon={Mic} lagging={lag(p.recorded)} boost={false} />
                    <Counter value={p.worked} Icon={CalendarDays} lagging={false} boost={false} />

                    {/* feat-105: last-2-weeks adherence % — pre-capped totals-basis pct from backend, "—" on null */}
                    <AdherenceCell pct={p.lesson_pct} />
                    <AdherenceCell pct={p.recorded_pct} />

                    {/* feat-057: hidden Accuracy cell — likely returns, see CHARTER.
                    <span className={`text-sm font-bold ${accuracyTone(p.accuracy)}`}>{p.accuracy_display}</span>
                    */}

                    {/* feat-057: hidden Times excused cell (PLACEHOLDER Slate dot) — likely returns, see CHARTER.
                    <button onClick={() => setSelected(p.server_id)} className="flex items-center gap-1.5" aria-label={`Times excused for ${p.name}`}>
                      {excused ? (
                        <>
                          <span className="text-sm font-bold text-ink">{p.times_excused}</span>
                          <EstimatedMarker variant="dot" />
                          <ChevronRight size={13} className="text-inkMuted" />
                        </>
                      ) : (
                        <span className="text-sm text-inkMuted/40">—</span>
                      )}
                    </button>
                    */}

                    {/* Status — base chip REAL (clean). P2-C removed the "Override · coming
                        soon" pill: the chip was always the real signal, so a behind row now
                        shows the StatusChip alone. */}
                    <div className="flex flex-col items-start gap-1.5">
                      {/* feat-195 D4b/AC1: the chip becomes the hover-card anchor —
                          hover, keyboard focus AND touch. `interactive` is gated on the
                          payload actually being there: with no status_reason the chip
                          renders exactly as it did before feat-195, no affordance at
                          all (ADR §B5 deploy window). */}
                      <StatusChip
                        kind={p.status}
                        interactive={Boolean(p.status_reason)}
                        tooltipId={STATUS_TOOLTIP_ID}
                        tooltipContent={p.server_id}
                      />
                      {/* feat-058: hidden with the Times-excused column (feat-057) — restore together */}
                      {/* {excused && <ExcusedQualifier />} */}
                    </div>

                    {/* Last active — REAL */}
                    <span className={`text-xs ${p.status === 'behind' ? 'text-coral' : 'text-inkMuted'}`}>{p.last_active_display}</span>
                  </div>
                );
              })}

              {rows.length === 0 && (
                <div className="px-5 py-12 text-center text-sm text-inkMuted font-sans">
                  No crew match this view. Clear the filter or search to see everyone.
                </div>
              )}
             </div>
            </div>

            {/* feat-195 — mounted HERE, as a sibling of the overflow-x-auto block and
                NOT inside min-w-[920px]. See STATUS_TOOLTIP_ID above for why both facts
                are load-bearing. positionStrategy stays at its default (`absolute`),
                matching the ShiftMetricsTable precedent: react-tooltip already listens
                to the anchor's scroll parent, so the card tracks its row when the roster
                scrolls horizontally.

                THE EVENT MAP IS EXPLICIT ON PURPOSE. AC1 requires hover AND keyboard AND
                touch. The obvious way to add touch — `openOnClick` — ZEROES `mouseover`
                and `focus` in react-tooltip's own source, silently removing the other two
                while still looking like it works. `escape` (keyboard dismiss) and
                `clickOutsideAnchor` (touch dismiss) are both OFF by default, so the
                default config does not satisfy "reachable on touch" either. */}
            <Tooltip
              id={STATUS_TOOLTIP_ID}
              place="top"
              clickable
              opacity={1}
              openEvents={{ mouseover: true, focus: true, click: true }}
              closeEvents={{ mouseout: true, blur: true }}
              globalCloseEvents={{ escape: true, clickOutsideAnchor: true, scroll: true }}
              style={{
                padding: 0,
                background: '#ffffff',
                borderRadius: '12px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                zIndex: 60,
                maxWidth: '340px',
              }}
              render={({ content }) => {
                const row = content ? rowsById.get(content) : undefined;
                if (!row || !row.status_reason) return null;
                return <StatusReasonCard reason={row.status_reason} status={row.status} />;
              }}
            />

            <p className="mt-4 px-1 text-xs text-inkMuted font-sans">
              Lessons, Recorded and Worked are all-time totals; Lesson %, Recorded % and Status all reflect the last 2 weeks.
              Worked counts only shifts completed (clocked out) in Square; Recorded counts the unique days on which at least one
              shift was recorded, so two recordings in one day count once. Status compares the lessons completed and the days
              recorded on in the last 2 weeks against the days worked in that same window — they don't have to fall on the same
              day. On track means each keeps pace with days worked (falling one day short is forgiven); doing more lessons or
              recorded days than days worked earns an Above &amp; beyond flag. A grey No recent shifts badge means the crew member had no completed Square
              shifts in the last 2 weeks, so they're set aside from the Behind / On track / Above counts until they're back on the
              clock. Crew who have completed every lesson in the curriculum are paced on recordings only. Hover or tab to any Status
              badge to see how that verdict was reached. Times-excused and the excuse log are seeded preview data (not yet recorded from your data); manager overrides
              land in a later release. Click any name for the full log.
            </p>
          </>
        ) : null}
      </main>

      {/* Detail modal — read-only (NO add form, NO Save; write deferred) */}
      {sel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setSelected(null)}>
          <div className="flex max-h-[85vh] w-full max-w-md flex-col rounded-2xl p-6 bg-white border border-softBorder shadow-xl font-sans" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div
                  className="flex h-11 w-11 items-center justify-center rounded-full text-sm font-bold text-white"
                  style={{ background: sel.status === 'behind' ? '#D55847' : sel.status === 'no_recent_shifts' ? '#717182' : '#5FA845' }}
                >
                  {sel.initials}
                </div>
                <div>
                  <div className="font-display text-lg text-ink">{sel.name}</div>
                  <div className="text-xs text-inkMuted">Crew</div>
                </div>
              </div>
              <button onClick={() => setSelected(null)} className="text-inkMuted hover:text-ink transition-colors" aria-label="Close">
                <X size={18} />
              </button>
            </div>

            {/* Scrollable body — header stays pinned, content (snapshot · activity ·
                excuse log) scrolls so a member with many active days never blows the
                viewport (feat-095 OQ3). */}
            <div className="flex-1 overflow-y-auto -mr-2 pr-2">
            {/* Status + real U·R·W snapshot */}
            <div className={`flex items-center justify-between rounded-xl px-4 py-3 bg-cream border border-softBorder ${sel.status_reason ? 'mb-3' : 'mb-5'}`}>
              <StatusChip kind={sel.status} />
              <div className="flex items-center gap-4 text-sm font-bold text-ink">
                <span className="flex items-center gap-1"><BookOpen size={13} className="text-coral" /> {sel.units}</span>
                <span className="flex items-center gap-1"><Mic size={13} className="text-gold" /> {sel.recorded}</span>
                <span className="flex items-center gap-1"><CalendarDays size={13} className="text-inkMuted" /> {sel.worked}</span>
              </div>
            </div>

            {/* feat-195 AC3/D4c — the SAME card, persistently, no hover required. One
                component, two presentations: the inline one drops the header band (the
                chip is already rendered directly above) and the floating chrome. This is
                also the accessible fallback path — nothing here is hover-gated. */}
            {sel.status_reason && (
              <div className="mb-5">
                <StatusReasonCard reason={sel.status_reason} status={sel.status} presentation="inline" />
              </div>
            )}

            {/* feat-095: Recent activity timeline — per-day Worked/Recorded/Lesson
                indicators + completed-unit title(s), newest-first, rendered VERBATIM
                from the backend feed (no bucketing, no %/day math —
                feedback_metrics_single_source). Degrades gracefully: a fetch failure
                (incl. the fail-closed 404/403) just hides the section, leaving the
                rest of the dialog rendered (AC#7). */}
            {!activityFailed && (activityLoading || (activity && activity.days.length > 0)) && (
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-inkMuted/60">
                    Recent activity · last 2 weeks
                  </span>
                </div>
                {activityLoading ? (
                  <div className="flex flex-col gap-2">
                    {[0, 1, 2].map((i) => (
                      <div key={i} className="h-12 rounded-lg bg-cream border border-softBorder animate-pulse" />
                    ))}
                  </div>
                ) : (
                  <div className="flex max-h-64 flex-col gap-2 overflow-y-auto pr-1">
                    {activity!.days.map((d) => (
                      <ActivityDayRow key={d.date} day={d} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Excuse history — PLACEHOLDER (panel-level Slate marker when entries exist) */}
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className="text-[10px] font-bold uppercase tracking-widest text-inkMuted/60">
                Excuse history · {sel.times_excused}
              </span>
              {sel.times_excused > 0 && <EstimatedMarker variant="panel" label="Sample data — not yet recorded" />}
            </div>
            {sel.excuse_history.length === 0 ? (
              <p className="text-sm text-inkMuted">No excuses on record.</p>
            ) : (
              <div className="flex flex-col gap-3">
                {sel.excuse_history.map((r, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-preview-light">
                      <Info size={14} className="text-preview" strokeWidth={2.5} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-ink">{r.reason}</div>
                      <div className="text-xs text-inkMuted">
                        {r.date} · by {r.manager_name} · at {r.base_units}·{r.base_recorded}·{r.base_worked}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingProgressPage;
